<template>
  <div
    :key="slug"
    data-testid="hero-video-card"
    class="flex h-full w-[512px] flex-shrink-0 flex-col rounded-lg shadow-pm"
  >
    <div
      class="relative flex flex-grow flex-col justify-between rounded-t-lg bg-s-100 p-3"
      :class="{ 'rounded-b-lg': !showProgressBar }"
    >
      <div class="absolute left-0 top-0 h-full w-full">
        <img
          data-testid="hero-video-card-cover"
          class="fade-in h-full w-full rounded-t-lg object-cover object-center"
          :class="{ 'rounded-b-lg': !showProgressBar }"
          :src="$cloudinary.resize(coverUrl, 'hero-video-card')"
          alt=""
        />
        <div
          class="absolute left-0 top-0 h-full w-full rounded-t-lg bg-black/60"
          :class="{ 'rounded-b-lg': !showProgressBar }"
        />
      </div>
      <div class="z-10 text-white">
        <CardLabel class="mb-4" type="mp4" />
        <div
          data-testid="hero-video-card-date"
          class="fade-in mb-2 text-xs font-semibold"
        >
          {{ publishedDate }} | {{ duration }}
        </div>
        <div
          data-testid="hero-video-card-title"
          class="fade-in mb-2 line-clamp-3 text-lg font-semibold"
        >
          {{ title }}
        </div>
        <div
          data-testid="hero-video-card-excerpt"
          class="fade-in line-clamp-2 text-xs"
        >
          {{ excerpt }}
        </div>
      </div>
      <VideoCardFooter
        :is-authorized="isAuthorized"
        :has-preview="hasPreview"
        :text-button="textButton"
        @play="$emit('play')"
      />
    </div>
    <ProgressBar v-if="showProgressBar" duration="10s" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CardLabel from '~/components/portals/CardLabel.vue'
import ProgressBar from '~/components/portals/ProgressBar.vue'
import VideoCardFooter from '~/components/portals/VideoCardFooter.vue'
import { useNuxtApp } from '#app'
import type { TVideoWithExtra } from '~/src/types/common'

export type THeroVideoCardProps = TVideoWithExtra & {
  excerpt: string
  isAuthorized: boolean
  showProgressBar?: boolean
}

const props = withDefaults(defineProps<THeroVideoCardProps>(), {
  coverUrl: '',
  previewVimeoId: '',
  showProgressBar: true,
})

const { $cloudinary } = useNuxtApp()
const hasPreview = computed(() => !!props.previewVimeoId)

const textButton = computed(() => {
  if (props.isAuthorized) return 'Watch Now'
  if (hasPreview.value) return 'Watch Preview'
  return 'Contact Us'
})
</script>

<style scoped>
.fade-in {
  animation: fade-in 0.5s ease-in-out;
}

@keyframes fade-in {
  from {
    opacity: 0.5;
  }

  to {
    opacity: 1;
  }
}
</style>
